import { defineComponent as _defineComponent } from 'vue'
import SingleStepCompetition from '@/pages/sports/single-step/SingleStepCompetition.vue';
import SingleStepTeam from '@/pages/sports/single-step/SingleStepTeam.vue';
import { Platform } from '@/@types/graphql-types';
import {
	GetSingleStepTeamOrCompetitionDocument,
	GetSingleStepTeamOrCompetitionQuery,
	GetSingleStepTeamOrCompetitionQueryVariables,
} from '@/features/sports/graphql/queries/GetSingleStepTeamOrCompetition.query';
import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useRoutingStore, useLanguageStore } from '@/helpers/composables/useStores';
import { useQuery } from '@/helpers/composables/useApollo';


export default /*#__PURE__*/_defineComponent({
  __name: 'SingleStepTeamOrCompetition',
  setup(__props) {

const route = useRoute();
const { country, language, localeObject } = useLanguageStore();
const { activeDomain } = useRoutingStore();

/*
	Data is fetched here but not passed down to children on purpose.
	The queries used for this page and both children all use the same fragments. The
	idea is that Apollo will then be able to cache everything appropriately so that
	when the correct child loads (team or competition page) it can take over the data
	intelligently without making another request.
	The main difference is competition pages dont have a `filter.startAt.to` field so
	they normally fetch more data. I prefered to opt for less data fetched.
*/
const queryVariables = computed<GetSingleStepTeamOrCompetitionQueryVariables>(() => {
	return {
		fullPath: route.path,
		site: activeDomain.value,
		language: language.value,
		country: country.value,
		platform: Platform.Web,
		first: 20, // matches "first" in competition pages; number of events to get,
		timezoneOffset: 0,
		filter: {
			startAt: {
				from: shiftDateWithTimezone(localeObject.value.timezone).toISOString(),
			},
			packages: [],
			competitionIds: [],
		},
	};
});

const { result } = useQuery<GetSingleStepTeamOrCompetitionQuery, GetSingleStepTeamOrCompetitionQueryVariables>(
	GetSingleStepTeamOrCompetitionDocument,
	queryVariables,
	() => ({
		enabled: ((route.meta?.data as string) ?? '').includes('singlestep'),
	})
);

const teamOrCompetitionData = computed(() => result.value?.urlV2);
const lockedRoute = ref(route);
const competitionId = computed(() => {
	if (teamOrCompetitionData?.value?.node.__typename === 'SportCompetitionV2') {
		return teamOrCompetitionData.value.node.objectId;
	}
	return undefined;
});

const pageObjectType = computed(() => {
	if (
		teamOrCompetitionData.value?.node.__typename === 'SportCompetitorV2' ||
		teamOrCompetitionData.value?.node.__typename === 'SportCompetitionV2'
	) {
		return teamOrCompetitionData.value.node?.objectType;
	}

	return undefined;
});

watch(
	() => route,
	() => {
		updateLockedRoute();
	},
	{ immediate: true }
);

watch(
	() => teamOrCompetitionData.value,
	() => {
		updateLockedRoute();
	},
	{ immediate: true }
);

// since route and teamOrCompetitionData query are not in sync with each other,
// we need ot make sure the underlying components get one route to rely on.
// otherwise components and computed fields will update unnecessarily and cause errors when navigating.
function updateLockedRoute() {
	if (route.fullPath === teamOrCompetitionData.value?.fullPath) {
		lockedRoute.value = route;
	}
}

return { __sfc: true,route, country, language, localeObject, activeDomain, queryVariables, result, teamOrCompetitionData, lockedRoute, competitionId, pageObjectType, updateLockedRoute, SingleStepCompetition, SingleStepTeam }
}

})